import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, Link as LinkIcon } from "lucide-react";
import { errMsg } from "../lib/api.js";
import { logError } from "../lib/logger.js";
import type { PolicyKind, PolicySigningStatus } from "../lib/policySigning.js";
import { PolicySignPanel, SignedPolicyDownloads } from "../components/PolicySignPanel.js";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card.js";

const API_BASE = "";

interface SignInfo extends PolicySigningStatus {
  alreadySigned: boolean;
  firstName?: string;
}

// Public page behind the signing link HR emails: the employee reads each
// document HR assigned, ticks to agree, signs with their name on record, and
// can download the signed copies straight away (or any time from the same link).
export default function HandbookSignPage() {
  const [params] = useSearchParams();
  const token = params.get("token") ?? "";

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [info, setInfo] = useState<SignInfo | null>(null);

  useEffect(() => {
    if (!token) { setError("No token provided."); setLoading(false); return; }
    axios.get<SignInfo>(`${API_BASE}/api/handbook/sign/${token}`)
      .then(({ data }) => setInfo(data))
      .catch((err) => { logError("HandbookSignPage.load", err); setError(errMsg(err) ?? "This link is invalid."); })
      .finally(() => setLoading(false));
  }, [token]);

  async function sign(kind: PolicyKind) {
    setError("");
    try {
      const { data } = await axios.post<SignInfo>(`${API_BASE}/api/handbook/sign/${token}`, { kind });
      setInfo(data);
    } catch (err) {
      logError("HandbookSignPage.submit", err);
      setError(errMsg(err) ?? "Failed to submit. Please try again.");
      throw err;
    }
  }

  if (loading) {
    return <div className="flex h-screen items-center justify-center text-[15px] text-surface-500">Loading…</div>;
  }

  if (error && !info) {
    return (
      <div className="flex h-screen items-center justify-center px-4">
        <div className="max-w-sm p-8 text-center">
          <LinkIcon size={48} className="mx-auto mb-4 text-surface-400" />
          <h2 className="mb-2 text-xl font-bold">Link Unavailable</h2>
          <p className="text-sm text-surface-600">{error}</p>
          <p className="mt-3 text-[13px] text-surface-400">Please contact HR to get a new link.</p>
        </div>
      </div>
    );
  }

  if (info && !info.required) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-surface-50 px-4">
        <Card className="w-full max-w-lg">
          <CardHeader className="items-center text-center">
            <CheckCircle2 size={56} className="mb-2 text-[var(--color-success-accent)]" />
            <CardTitle>{info.alreadySigned ? "All signed" : "Thank you!"}</CardTitle>
            <CardDescription>Your signed copies are below. Download them for your records.</CardDescription>
          </CardHeader>
          <CardContent><SignedPolicyDownloads signed={info.signed} /></CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-surface-50 pb-24">
      <div className="border-b border-surface-200 bg-surface-0 px-6 py-4">
        <h1 className="text-lg font-bold">Sign your documents</h1>
        {info?.firstName && <p className="mt-0.5 text-[13px] text-surface-600">Hi {info.firstName}, HR has asked you to read and sign the documents below.</p>}
      </div>

      <div className="mx-auto my-6 max-w-3xl space-y-5 px-4">
        {error && <p role="alert" className="text-sm text-[var(--color-error-fg)]">{error}</p>}
        {info && info.signed.length > 0 && (
          <Card>
            <CardHeader className="pb-3"><CardTitle className="text-[15px]">Signed</CardTitle></CardHeader>
            <CardContent><SignedPolicyDownloads signed={info.signed} /></CardContent>
          </Card>
        )}
        {info && <PolicySignPanel outstanding={info.outstanding} signerName={info.signerName} onSign={sign} />}
      </div>
    </div>
  );
}
